import { Directive } from "vue";

const DURATION = 500;
const ABORT_EVENTS = [
    "mousedown",
    "wheel",
    "DOMMouseScroll",
    "mousewheel",
    "keyup",
    "touchmove",
];

let cancelActiveScroll: (() => void) | undefined;

type ScrollToState = {
    selector: string;
    handleClick: (event: Event) => void;
};

const states = new WeakMap<HTMLElement, ScrollToState>();

function cubicBezierCoordinate(t: number, first: number, second: number) {
    const inverse = 1 - t;
    return (
        3 * inverse * inverse * t * first +
        3 * inverse * t * t * second +
        t * t * t
    );
}

function cubicBezierSlope(t: number, first: number, second: number) {
    const inverse = 1 - t;
    return (
        3 * inverse * inverse * first +
        6 * inverse * t * (second - first) +
        3 * t * t * (1 - second)
    );
}

// Equivalent to CSS `ease`: cubic-bezier(0.25, 0.1, 0.25, 1).
export function easeScroll(progress: number) {
    if (progress <= 0) return 0;
    if (progress >= 1) return 1;

    let curvePosition = progress;
    for (let iteration = 0; iteration < 6; iteration += 1) {
        const slope = cubicBezierSlope(curvePosition, 0.25, 0.25);
        curvePosition -=
            (cubicBezierCoordinate(curvePosition, 0.25, 0.25) - progress) /
            slope;
    }

    return cubicBezierCoordinate(curvePosition, 0.1, 1);
}

function scrollTo(selector: string) {
    const target = document.querySelector(selector);
    if (!target) {
        console.warn(`[scroll-to] Could not find element: ${selector}`);
        return;
    }

    cancelActiveScroll?.();

    const startX = window.scrollX;
    const startY = window.scrollY;
    const targetY = startY + target.getBoundingClientRect().top;
    const distance = targetY - startY;

    if (!distance) return;

    let animationFrame = 0;
    let finished = false;

    const removeAbortListeners = () => {
        for (const eventName of ABORT_EVENTS) {
            window.removeEventListener(eventName, cancel);
        }
    };

    const cancel = () => {
        if (finished) return;

        finished = true;
        window.cancelAnimationFrame(animationFrame);
        removeAbortListeners();
        if (cancelActiveScroll === cancel) cancelActiveScroll = undefined;
    };

    for (const eventName of ABORT_EVENTS) {
        window.addEventListener(eventName, cancel, { passive: true });
    }
    cancelActiveScroll = cancel;

    const startedAt = performance.now();
    const step = (timestamp: number) => {
        const progress = Math.min((timestamp - startedAt) / DURATION, 1);
        window.scrollTo(startX, startY + distance * easeScroll(progress));

        if (progress < 1) {
            animationFrame = window.requestAnimationFrame(step);
            return;
        }

        finished = true;
        removeAbortListeners();
        if (cancelActiveScroll === cancel) cancelActiveScroll = undefined;
    };

    animationFrame = window.requestAnimationFrame(step);
}

const scrollToDirective: Directive<HTMLElement, string> = {
    beforeMount(element, binding) {
        const state: ScrollToState = {
            selector: binding.value,
            handleClick: () => undefined,
        };

        state.handleClick = (event) => {
            if (!state.selector) return;

            event.preventDefault();
            scrollTo(state.selector);
        };

        states.set(element, state);
        element.addEventListener("click", state.handleClick);
    },
    updated(element, binding) {
        const state = states.get(element);
        if (state) state.selector = binding.value;
    },
    unmounted(element) {
        const state = states.get(element);
        if (!state) return;

        element.removeEventListener("click", state.handleClick);
        states.delete(element);
    },
};

export default scrollToDirective;
